import { z } from "zod";
import { email, money, trimmed, uuid } from "./common";
import { emailCode } from "./auth";
import { siteContactsSchema, type PublicContacts } from "./site";

/** SaaS-биллинг и Platform Admin (ТЗ 03 §5–§10). */

export const BILLING_PERIODS = ["month", "quarter", "year"] as const;
export type BillingPeriod = (typeof BILLING_PERIODS)[number];
export const PLATFORM_ROLES = ["owner", "admin", "billing", "sales", "support", "auditor"] as const;
export type PlatformRole = (typeof PLATFORM_ROLES)[number];

/** Права ролей платформы (ТЗ 03 §8.2). */
export const PLATFORM_PERMISSIONS = {
  "metrics.view": ["owner", "admin", "billing", "sales", "support", "auditor"],
  "companies.view": ["owner", "admin", "billing", "sales", "support", "auditor"],
  "companies.create": ["owner", "admin", "sales"],
  "companies.update": ["owner", "admin", "sales"],
  /** Назначать и менять менеджера клиента: менеджер по продажам сам себе клиентов не переназначает */
  "companies.assign_manager": ["owner", "admin"],
  "subscriptions.manage": ["owner", "admin", "sales"],
  "entitlements.override": ["owner", "admin"],
  "billing.view": ["owner", "admin", "billing", "auditor"],
  "billing.manage": ["owner", "billing"],
  "impersonate": ["owner", "admin", "support"],
  "catalog.manage": ["owner"],
  "staff.manage": ["owner"],
  "audit.view": ["owner", "admin", "auditor"],
  "settings.manage": ["owner"],
  "support.view": ["owner", "admin", "sales", "support"],
  "leads.view": ["owner", "admin", "sales", "auditor"],
  "leads.manage": ["owner", "admin", "sales"],
  "support.reply": ["owner", "admin", "support"],
  /** Публиковать версии десктоп-приложений (касса Kafe Coco) */
  "releases.manage": ["owner", "admin"],
  /** Рассылки клиентам: смотреть историю и отправлять сообщения в ERP / Telegram */
  "broadcasts.view": ["owner", "admin", "sales", "support", "auditor"],
  "broadcasts.send": ["owner", "admin", "support"],
} as const satisfies Record<string, readonly PlatformRole[]>;
export type PlatformPermission = keyof typeof PLATFORM_PERMISSIONS;
export const platformCan = (role: string, p: PlatformPermission) => (PLATFORM_PERMISSIONS[p] as readonly string[]).includes(role);

// ───────── вход ─────────

export const platformLoginSchema = z.strictObject({ email: z.string().trim().toLowerCase().email({ error: "Некорректный email" }), password: z.string().min(1, { error: "Введите пароль" }) });
export const platform2faSchema = z.strictObject({ challenge: z.string().min(10), code: z.string().regex(/^\d{6}$/, { error: "Код — 6 цифр" }) });

// ───────── изменение подписки ─────────

/** Срок дополнения: без дат — вместе с подпиской (до конца оплаченного срока, дальше продлевается её счетами); с датами — на свой срок, не продлевается. */
const addonTerm = {
  /** Первый день (YYYY-MM-DD, пояс платформы), не раньше сегодня */
  from: z.iso.date().optional(),
  /** Последний день включительно */
  to: z.iso.date().optional(),
};
const termOk = (v: { from?: string; to?: string }) => !v.from === !v.to;
const termOrder = (v: { from?: string; to?: string }) => !v.from || !v.to || v.to >= v.from;
const addonChange = z.strictObject({
  code: z.string().min(1).max(60),
  action: z.enum(["enable", "disable"]),
  quantity: z.number().int().min(1).max(1000).default(1),
  ...addonTerm,
})
  .refine(termOk, { error: "Укажите обе даты", path: ["to"] })
  .refine(termOrder, { error: "Дата окончания раньше начала", path: ["to"] });
/** Предпросмотр/применение: смена тарифа, периода, add-ons (ТЗ 03 §6.2). */
export const subscriptionChangeSchema = z.strictObject({
  planCode: z.string().min(1).max(40).optional(),
  period: z.enum(BILLING_PERIODS).optional(),
  addons: z.array(addonChange).max(30).default([]),
});
export type SubscriptionChange = z.output<typeof subscriptionChangeSchema>;
/**
 * Клиент выбирает тариф в ERP — счёт на оплату: за месяц/квартал/год (цены периода, квартал и год — со скидкой)
 * или по выбранную дату (`custom` + `until`). После оплаты — этот тариф и оплаченный срок.
 */
export const planInvoiceSchema = z.strictObject({
  planCode: z.string().min(1).max(40),
  period: z.enum([...BILLING_PERIODS, "custom"]),
  /** Для custom: оплачено по эту дату включительно (YYYY-MM-DD) */
  until: z.iso.date().optional(),
}).refine((v) => v.period !== "custom" || !!v.until, { error: "Укажите дату окончания", path: ["until"] });
export type PlanInvoiceInput = z.output<typeof planInvoiceSchema>;

/** Платформа: включение add-on (бесплатно — причина и срок обязательны, BR-SaaS-06). */
export const addonEnableSchema = z.strictObject({
  quantity: z.number().int().min(1).max(1000).default(1),
  billing: z.enum(["charge", "free"]),
  reason: z.string().trim().max(300).optional(),
  until: z.iso.datetime({ offset: true }).nullable().optional(),
  trialDays: z.number().int().min(1).max(90).optional(),
  ...addonTerm,
})
  .refine((v) => v.billing === "charge" || !!v.reason?.trim(), { error: "Укажите причину", path: ["reason"] })
  .refine(termOk, { error: "Укажите обе даты", path: ["to"] })
  .refine(termOrder, { error: "Дата окончания раньше начала", path: ["to"] });
export const addonDisableSchema = z.strictObject({ when: z.enum(["now", "period_end"]), reason: trimmed(1, 300) });
export const subscriptionExtendSchema = z.strictObject({ days: z.number().int().min(1).max(366), reason: trimmed(1, 300) });
export const reasonSchema = z.strictObject({ reason: trimmed(1, 300) });
export const overrideSchema = z.strictObject({
  featureCode: z.string().min(3).max(60),
  mode: z.enum(["grant", "revoke", "set", "delta"]),
  value: z.number().nullable().optional(),
  reason: trimmed(1, 300),
  expiresAt: z.iso.datetime({ offset: true }).nullable().optional(),
});

// ───────── счета ─────────

export const markPaidSchema = z.strictObject({
  method: z.enum(["bank_transfer", "card", "qr", "manual"]).default("bank_transfer"),
  /** Номер платёжки/транзакции — идемпотентность (AC-SaaS-07) */
  reference: z.string().trim().min(1).max(100).optional(),
  amount: money.optional(),
});
/**
 * Счёт на продление из Platform Admin: за месяц/квартал/год (дальше счета — за этот период) или по выбранную дату,
 * со скидкой процентом и/или суммой. Без полей — как автоматический счёт.
 */
export const renewalInvoiceSchema = z.strictObject({
  period: z.enum([...BILLING_PERIODS, "custom"]).optional(),
  /** Для custom: оплачено по эту дату включительно (YYYY-MM-DD) */
  until: z.iso.date().optional(),
  discountPercent: z.number().min(0).max(100).optional(),
  discountAmount: money.optional(),
  comment: z.string().trim().max(300).optional(),
  dueDays: z.number().int().min(0).max(90).optional(),
}).refine((v) => v.period !== "custom" || !!v.until, { error: "Укажите дату окончания", path: ["until"] });
export type RenewalInvoiceInput = z.output<typeof renewalInvoiceSchema>;
export const manualInvoiceSchema = z.strictObject({
  description: trimmed(1, 200),
  amount: money.refine((v) => v > 0, { error: "Больше 0" }),
  dueDays: z.number().int().min(0).max(90).default(7),
});

// ───────── компании и каталог ─────────

/** Новый клиент, шаг 1: код на email владельца; шаг 2 — проверка кода → `emailToken` (1 ч) для создания. */
export const platformClientEmailSchema = z.strictObject({ email });
export const platformClientEmailVerifySchema = z.strictObject({ email, code: emailCode });
export type PlatformClientEmailVerified = { email: string; emailToken: string; expiresInSec: number };

export const platformCompanyCreateSchema = z.strictObject({
  companyName: trimmed(2, 120),
  ownerName: trimmed(2, 120),
  ownerEmail: z.string().trim().toLowerCase().email({ error: "Некорректный email" }),
  planCode: z.string().min(1).max(40),
  trialDays: z.number().int().min(0).max(90).default(14),
  currency: z.string().regex(/^[A-Z]{3}$/).default("KGS"),
  country: z.string().regex(/^[A-Z]{2}$/).default("KG"),
  /** Менеджер клиента; менеджер по продажам без права назначать — закрепляется за собой */
  managerId: z.uuid().nullable().optional(),
  /** Подтверждённый email владельца (`POST /companies/email-verify`) — должен совпадать с ownerEmail */
  emailToken: z.string().min(20).max(200),
});
export const platformCompaniesQuery = z.object({
  q: z.string().trim().max(100).optional(),
  status: z.string().max(40).optional(),
  plan: z.string().max(40).optional(),
  page: z.coerce.number().int().min(1).default(1),
  pageSize: z.coerce.number().int().min(1).max(200).default(50),
});
const priceList = z.array(z.strictObject({ currency: z.string().regex(/^[A-Z]{3}$/), period: z.enum(BILLING_PERIODS), amount: money })).max(30);
export const planVersionSchema = z.strictObject({
  features: z.array(z.string()).max(80),
  limits: z.record(z.string(), z.number().int().min(0).nullable()),
  trialDays: z.number().int().min(0).max(90),
  prices: priceList,
});
export const addonSchema = z.strictObject({
  code: z.string().regex(/^[a-z0-9_.]+$/, { error: "Латиница, цифры, точка и _" }).max(60),
  name: trimmed(1, 120),
  description: z.string().trim().max(500).nullable().optional(),
  grantsFeatures: z.array(z.string()).max(20).default([]),
  limitDeltas: z.record(z.string(), z.number().int()).default({}),
  pricingModel: z.enum(["flat", "per_unit"]).default("flat"),
  unitLabel: z.string().trim().max(30).nullable().optional(),
  requiresAddons: z.array(z.string()).max(10).default([]),
  trialDays: z.number().int().min(0).max(90).default(0),
  isPublic: z.boolean().default(true),
  prices: priceList.default([]),
  availability: z.record(z.string(), z.enum(["included", "purchasable", "unavailable"])).default({}),
});
export const platformSettingsSchema = z.strictObject({
  trialDays: z.number().int().min(0).max(90),
  graceDays: z.number().int().min(0).max(60),
  readOnlyDays: z.number().int().min(0).max(90),
  invoiceLeadDays: z.number().int().min(0).max(30),
  invoiceDueDays: z.number().int().min(0).max(60),
  bankDetails: z.string().trim().max(2000).nullable().optional(),
  /** Контакты и соцсети Coca на лендинге (подвал, schema.org); не передано — не меняются. */
  contacts: siteContactsSchema.nullable().optional(),
});
/** Публичные данные платформы для лендинга (`GET /public/v1/platform`). */
export type PublicPlatform = { contacts: PublicContacts };
export const platformStaffSchema = z.strictObject({
  email: z.string().trim().toLowerCase().email({ error: "Некорректный email" }),
  name: trimmed(2, 120),
  role: z.enum(PLATFORM_ROLES),
  password: z.string().min(10, { error: "Пароль — минимум 10 символов" }).max(128),
});
export const impersonateSchema = z.strictObject({ mode: z.enum(["read_only", "full"]).default("read_only"), reason: trimmed(3, 300) });
/**
 * Сеанс поддержки — только с согласия клиента: запрос (pending) → владелец или управляющий (`support.grant_access`)
 * разрешает (approved) или отклоняет (denied) → сотрудник платформы входит (active) → выход (ended). Отозвал запрос сам — cancelled.
 * Срок: ответить — `IMPERSONATION_REQUEST_MINUTES`, войти после разрешения — `IMPERSONATION_START_MINUTES`, сеанс — 60 мин.
 */
export const IMPERSONATION_STATUSES = ["pending", "approved", "denied", "active", "ended", "cancelled"] as const;
export type ImpersonationStatus = (typeof IMPERSONATION_STATUSES)[number] | "expired";
export const IMPERSONATION_REQUEST_MINUTES = 15;
export const IMPERSONATION_START_MINUTES = 10;
export const metricsQuery = z.object({ from: z.iso.date().optional(), to: z.iso.date().optional() });
export { uuid as platformUuid };

// ───────── финансы платформы: продажи, плата за подключение, сотрудники, табель, деньги ─────────

/** Часовой пояс отчётов платформы: «день» оплаты и операции считается по нему. */
export const PLATFORM_TIMEZONE = "Asia/Bishkek";

/** Разовая плата за подключение тарифа (по валютам). Берётся в первом платном счёте; при переходе на тариф дороже — разница. */
export const planSetupFeesSchema = z.strictObject({
  fees: z.array(z.strictObject({ currency: z.string().regex(/^[A-Z]{3}$/), amount: money })).max(10),
});
/** Роли, которые могут быть менеджером клиента */
export const MANAGER_ROLES = ["owner", "admin", "sales"] as const;
/**
 * Смена менеджера: действует с сегодняшнего дня (с момента смены) или с будущей даты — задним числом нельзя,
 * оплаты до смены остаются за прежним менеджером.
 */
export const companyManagerSchema = z.strictObject({
  managerId: z.uuid().nullable(),
  /** Дата начала (YYYY-MM-DD, пояс платформы); нет или сегодня — с момента смены */
  effectiveFrom: z.iso.date().optional(),
  reason: z.string().trim().max(300).optional(),
});
export const platformStaffPatchSchema = z.strictObject({
  name: trimmed(2, 120).optional(),
  role: z.enum(PLATFORM_ROLES).optional(),
  /** Процент от оплат закреплённых клиентов (вознаграждение менеджера) */
  commissionPercent: z.number().min(0).max(100).optional(),
});
const periodQuery = {
  from: z.iso.date().optional(),
  to: z.iso.date().optional(),
  currency: z.string().regex(/^[A-Z]{3}$/).optional(),
};
export const salesQuery = z.object({ ...periodQuery, group: z.enum(["day", "month"]).default("day") });
export const staffFinanceQuery = z.object(periodQuery);

/** Табель оплат: состояние клиента по оплатам. */
export const PAYMENT_STATES = ["paid", "due_soon", "awaiting", "overdue", "trial", "blocked", "closed"] as const;
export type PaymentState = (typeof PAYMENT_STATES)[number];
export const paymentScheduleQuery = z.object({
  /** Первый месяц табеля, YYYY-MM; по умолчанию окно заканчивается следующим месяцем */
  from: z.string().regex(/^\d{4}-(0[1-9]|1[0-2])$/).optional(),
  months: z.coerce.number().int().min(1).max(12).default(6),
  state: z.enum(PAYMENT_STATES).optional(),
  q: z.string().trim().max(100).optional(),
  managerId: z.uuid().optional(),
  page: z.coerce.number().int().min(1).default(1),
  pageSize: z.coerce.number().int().min(1).max(200).default(50),
});

/** Денежные операции платформы: оплаты клиентов приходят сами, остальное вносится вручную. */
export const PLATFORM_CASH_CATEGORIES = {
  income: ["other_income", "investment"],
  expense: ["salary", "commission", "marketing", "servers", "rent", "taxes", "refund", "other_expense"],
} as const;
export type PlatformCashCategory = (typeof PLATFORM_CASH_CATEGORIES)["income" | "expense"][number];
export const PLATFORM_CASH_METHODS = ["cash", "bank", "card"] as const;
export const platformCashOperationSchema = z.strictObject({
  type: z.enum(["income", "expense"]),
  category: z.enum([...PLATFORM_CASH_CATEGORIES.income, ...PLATFORM_CASH_CATEGORIES.expense]),
  amount: money.refine((v) => v > 0, { error: "Больше 0" }),
  currency: z.string().regex(/^[A-Z]{3}$/).default("KGS"),
  method: z.enum(PLATFORM_CASH_METHODS).default("bank"),
  date: z.iso.date(),
  comment: z.string().trim().max(500).nullable().optional(),
  /** Кому выплачено (зарплата, вознаграждение) */
  staffId: z.uuid().nullable().optional(),
  /** Какой компании (возврат денег клиенту) */
  companyId: z.uuid().nullable().optional(),
}).refine((v) => (PLATFORM_CASH_CATEGORIES[v.type] as readonly string[]).includes(v.category), { error: "Категория не подходит к типу операции", path: ["category"] })
  .refine((v) => !["salary", "commission"].includes(v.category) || !!v.staffId, { error: "Выберите сотрудника", path: ["staffId"] });
export const platformCashQuery = z.object({
  ...periodQuery,
  type: z.enum(["income", "expense"]).optional(),
  source: z.enum(["payment", "manual"]).optional(),
  staffId: z.uuid().optional(),
  page: z.coerce.number().int().min(1).default(1),
  pageSize: z.coerce.number().int().min(1).max(200).default(50),
});
